import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * File Collection Evidence Documents Step
 *
 * Files documents harvested from an operator run (e.g. portal backup files a
 * `web_browser` action downloaded) as evidence on a collection thread, and
 * records each filing on the thread activity timeline.
 *
 * Each document must carry the OPERATOR_DOWNLOAD association written by
 * OperatorDownloadService — that provenance is the access check (the service
 * rejects arbitrary document ids). Idempotent per (thread, document): re-runs
 * and retries are no-ops.
 */
export const fileCollectionEvidenceDocumentsStep = defineStepConfig({
  type: "file_collection_evidence_documents",
  name: "File Collection Evidence Documents",
  description:
    "Files operator-downloaded documents (e.g. portal backup) as evidence on a collection thread",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    threadId: z.uuid().describe("Collection thread to file the documents onto"),
    documentIds: z
      .array(z.string())
      .default([])
      .describe(
        "Document ids harvested from an operator run (e.g. web_browser downloads). Wire from web_browser's documentIds output. Each must carry an OPERATOR_DOWNLOAD association.",
      ),
    actorUserId: z
      .uuid()
      .nullable()
      .optional()
      .describe(
        "The org user who approved the action. Omitted/null falls back to the run's triggering user, else files as the system actor.",
      ),
  }),

  outputSchema: z.object({
    filedCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of documents newly filed onto the thread"),
    filings: z
      .array(
        z.object({
          documentId: z.string(),
          /** False when the document was already filed on this thread. */
          filed: z.boolean(),
        }),
      )
      .describe("Per-document filing outcomes"),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
